import {PureComponent} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {format, parseISO, parse, isValid, differenceInDays, differenceInMonths, differenceInYears} from 'date-fns'
import Rating from 'react-rating'
import ChevronLeft from 'react-feather/dist/icons/chevron-left.js'
import ChevronRight from 'react-feather/dist/icons/chevron-right.js'
import X from 'react-feather/dist/icons/x.js'
import Send from 'react-feather/dist/icons/send.js'
import Linkedin from 'react-feather/dist/icons/linkedin.js'
import File from 'react-feather/dist/icons/file.js'
import Star from 'react-feather/dist/icons/star.js'

import api from '../../../../../../api.js'
import {generate} from '../../../../../routes.js'
import Availability from '../../../../../components/Freelance/Component/Availability/Availability.jsx'
import Picture from '../../../../../components/Picture/Picture.jsx'
import Price from '../../../../../components/Freelance/Component/Price/Price.jsx'
import Skills from '../../../../../components/Skills/Skills.jsx'
import ContactModal from '../../../../../components/Modals/ContactModal/ContactModal.jsx'
import connectModal from '../../../../../../shared/components/Modals/connect.js'
import {downloadFile} from '../../../../../services/downloadService.js'

import './FreelanceSearchIASidePanel.scss'

const tryParseDate = (dateStr) => {
  let d = parseISO(dateStr)
  if (isValid(d)) return d
  d = parse(dateStr, 'yyyy-MM-dd HH:mm:ss', new Date())
  if (isValid(d)) return d
  return null
}

const relativeDateCaption = (dateStr) => {
  if (!dateStr) return null
  try {
    const date = tryParseDate(dateStr)
    if (!date) return null
    const today = new Date()
    const diffDays = differenceInDays(date, today)

    let label
    let modifier

    if (diffDays === 0) {
      label = "Aujourd'hui"
      modifier = 'past'
    } else {
      const absDays = Math.abs(diffDays)
      const isPast = diffDays < 0
      modifier = isPast ? 'past' : 'future'

      if (absDays < 60) {
        label = `${absDays} jour${absDays > 1 ? 's' : ''}`
      } else {
        const absMonths = Math.abs(differenceInMonths(date, today))
        if (absMonths < 24) {
          label = `${absMonths} mois`
        } else {
          const absYears = Math.abs(differenceInYears(date, today))
          label = `${absYears} an${absYears > 1 ? 's' : ''}`
        }
      }

      label = isPast ? `Il y a ${label}` : `Dans ${label}`
    }

    return <span className={`form-caption form-caption--${modifier}`}>{label}</span>
  } catch {
    return null
  }
}

const emptyStar = () => <Star size={18} strokeWidth={0} fill="#bdc8d8" />
const fullStar = () => <Star size={18} strokeWidth={0} fill="#FF942D" />

const experienceDuration = (experience) => {
  const start = tryParseDate(experience?.start)
  if (!start) return null
  const end = experience?.end ? tryParseDate(experience.end) : new Date()
  if (!end) return null

  const months = Math.max(0, differenceInMonths(end, start))
  if (months < 12) {
    return {label: `${months} mois`, iso: `P${months}M`}
  }

  const years = Math.floor(months / 12)
  return {label: `${years} an${years > 1 ? 's' : ''}`, iso: `P${years}Y`}
}

class FreelanceSearchIASidePanel extends PureComponent {
  static propTypes = {
    currentUser: PropTypes.object,
    freelance: PropTypes.object,
    isAdmin: PropTypes.bool,
    matchPercentage: PropTypes.number,
    index: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    onPrev: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
  }

  static defaultProps = {
    isAdmin: false,
    matchPercentage: null,
  }

  constructor(props) {
    super(props)
    this.state = {
      ratings: {},
      downloadingDc: false,
    }
  }

  componentDidMount() {
    this.loadRatings(this.props.freelance)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.freelance?.id !== this.props.freelance?.id) {
      this.loadRatings(this.props.freelance)
    }
  }

  componentWillUnmount() {
    this._unmounted = true
  }

  loadRatings = async (freelance) => {
    const freelanceId = freelance?.id
    if (!freelanceId || !this.props.isAdmin) return

    this.setState({ratings: {}})

    try {
      const response = await api.freelances.reviews.getAll({freelance: freelanceId})
      if (this._unmounted || freelanceId !== this.props.freelance?.id) return

      const reviewSkills = response?.data?.reviews?.[0]?.skills ?? []
      const ratings = reviewSkills.reduce((acc, reviewSkill) => {
        if (reviewSkill?.skill?.id) acc[reviewSkill.skill.id] = reviewSkill.rate
        return acc
      }, {})

      this.setState({ratings})
    } catch (e) {
      console.warn(e)
    }
  }

  openContactModal = (user, freelance) => {
    this.props.openContactModal({ user, freelance })
  }

  downloadCvNormalized = (anonymized = false, whiteMarking = false) => {
    const {freelance} = this.props
    return api.freelances.cv.getNormalized({
      freelance: freelance.id,
      anonymized: anonymized || false,
      whiteMarking: whiteMarking || false
    })
      .then((blob) => {
        const firstname = freelance?.user?.firstname ?? ''
        const freelanceId = freelance?.id ?? 0
        const dateFormatted = format(new Date(), 'yyyyMMdd')
        const name = `${dateFormatted}-CV_Twineeds-${firstname}-${freelanceId}`
        return downloadFile(blob, name, 'pdf')
      })
      .catch((err) => console.warn(err))
  }

  onCvNormalizedDownloaded = async (anonymized = false, whiteMarking = false) => {
    this.setState({downloadingDc: true})
    this.downloadCvNormalized(anonymized, whiteMarking)
      .finally(() => {
        this.setState({downloadingDc: false})
      })
  }

  render() {
    const {freelance, currentUser, isAdmin, matchPercentage, index, total, onPrev, onNext, onClose} = this.props

    if (!freelance) return null

    const {user} = freelance
    const canViewPhoto = currentUser && (isAdmin || currentUser.account_type !== 'freelance' || currentUser.id === user.id)
    const image = canViewPhoto ? user?.image?.square ?? null : null
    const masterSkills = freelance?.master_skills ?? []
    const expertises = freelance?.expertises ?? []
    const secteurs = freelance?.secteurs ?? []
    const experiences = freelance?.experiences ?? []
    const ratedSkills = masterSkills
      .map(skill => ({...skill, rate: this.state.ratings[skill.id]}))
      .filter(skill => typeof skill.rate === 'number')
    const profileUrl = freelance?.id ? generate({name: 'freelance-profile', parameters: {slug: freelance.id}}) : null

    return (
      <div className="freelance-search-ia-side-panel">
        <div className="freelance-search-ia-side-panel__overlay" onClick={onClose} />

        <div className="freelance-search-ia-side-panel__inner">
          <div className="freelance-search-ia-side-panel__header">
            {total > 1 ? (
              <div className="freelance-search-ia-side-panel__nav">
                <button type="button" className="btn btn-sm btn-outline-secondary btn-auto-width" onClick={onPrev}>
                  <ChevronLeft size={24} />
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary btn-auto-width" onClick={onNext}>
                  <ChevronRight size={24} />
                </button>
                <span className="freelance-search-ia-side-panel__count">{index + 1} / {total}</span>
              </div>
            ) : <div />}

            <button type="button" className="btn btn-sm btn-outline-secondary btn-auto-width freelance-search-ia-side-panel__close" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className="freelance-search-ia-side-panel__identity">
            <Picture
              src={image}
              alt={user?.fullname ?? ''}
              isGold={user?.gold}
              round={false}
              className="freelance-search-ia-side-panel__avatar"
            />

            <div className="freelance-search-ia-side-panel__informations">
              <h2 className="freelance-search-ia-side-panel__fullname">
                {user?.fullname ?? ''}
                <span className="freelance-search-ia-side-panel__id">#{freelance.id}</span>
              </h2>

              <strong className="freelance-search-ia-side-panel__title">
                {freelance.title ?? ''}
              </strong>

              {matchPercentage !== null && (
                <span className={`badge badge-${matchPercentage > 70 ? 'success' : matchPercentage > 40 ? 'warning' : 'danger'}`}>
                  Match idéal ~ {matchPercentage}%
                </span>
              )}
            </div>
          </div>

          <div className="freelance-search-ia-side-panel__actions">
            <button
              type="button"
              className="btn btn-secondary flex-grow-1 justify-content-center"
              onClick={() => this.openContactModal(user, freelance)}
            >
              <Send size={16} />
              Contacter
            </button>

            <a
              href={freelance?.cv?.original ?? '#'}
              target={freelance?.cv?.original ? '_blank' : undefined}
              rel="noopener noreferrer"
              className={`btn btn-outline-shy${!freelance?.cv?.original ? ' disabled' : ''} btn-auto-width justify-content-center`}
              onClick={(e) => { if (!freelance?.cv?.original) e.preventDefault() }}
            >
              <File size={16} className="mb-1" />
              { freelance?.cv?.original ? "CV" : "Aucun CV" }
            </a>

            <a
              href={'#'}
              rel="noopener noreferrer"
              className={`btn btn-outline-shy${this.state.downloadingDc ? ' disabled' : ''} btn-auto-width justify-content-center`}
              onClick={(e) => {
                e.preventDefault()
                if (!this.state.downloadingDc) this.onCvNormalizedDownloaded(false, false)
              }}
            >
              <File size={16} className="mb-1" />
              DC
            </a>

            {user?.linkedin_url && (
              <a
                href={user.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-auto-width justify-content-center"
              >
                <Linkedin size={16} fill="white" />
              </a>
            )}
          </div>

          <ul className="freelance-search-ia-side-panel__grid">
            <li>
              <p>TJM</p>
              <strong><Price freelance={freelance} /></strong>
            </li>

            <li>
              <p>Disponibilité</p>
              <strong><Availability freelance={freelance} prefix={false} /></strong>
              {relativeDateCaption(freelance?.date_available)}
            </li>

            {user?.city?.label ? (
              <li>
                <p>Localisation</p>
                <strong>{user.city.label}</strong>
              </li>
            ) : null}
          </ul>

          {masterSkills.length || expertises.length ? (
            <div className="freelance-search-ia-side-panel__section">
              <h3>Compétences clés</h3>
              {masterSkills.length ? <Skills forceNoLink={true} list={masterSkills} secondary={true} /> : null}
              {expertises.length ? <Skills forceNoLink={true} list={expertises} secondary={true} outline={true} /> : null}
            </div>
          ) : null}

          {secteurs.length ? (
            <div className="freelance-search-ia-side-panel__section">
              <h3>Principaux secteurs</h3>
              <Skills forceNoLink={true} list={secteurs} shy={true} outline={true} />
            </div>
          ) : null}

          {ratedSkills.length ? (
            <div className="freelance-search-ia-side-panel__section">
              <h3>Expertises métiers</h3>
              <ul className="freelance-search-ia-side-panel__ratings">
                {ratedSkills.map(skill => (
                  <li key={skill.id}>
                    <span>{skill.name}</span>
                    <Rating
                      readonly={true}
                      initialRating={skill.rate}
                      fractions={2}
                      start={0}
                      stop={5}
                      emptySymbol={emptyStar()}
                      fullSymbol={fullStar()}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {experiences.length ? (
            <div className="freelance-search-ia-side-panel__section">
              <h3>Expériences récentes</h3>
              <ul className="freelance-search-ia-side-panel__experiences">
                {experiences.map(experience => {
                  if (!experience.title) return null
                  const duration = experienceDuration(experience)

                  return (
                    <li key={experience.id}>
                      <div>
                        <strong>{experience.title}</strong>
                        {experience.company ? (<span>{experience.company}</span>) : null}
                      </div>
                      {duration && <time dateTime={duration.iso}>{duration.label}</time>}
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : null}

          {profileUrl && (
            <div className="freelance-search-ia-side-panel__full-profile">
              <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary btn-block"
              >
                Voir la fiche complète
              </a>
              <span className="form-caption text-center">Détail des missions, formations, certifications, langues &amp; outils</span>
            </div>
          )}
        </div>
      </div>
    )
  }
}

export default connect()(connectModal({ContactModal}, FreelanceSearchIASidePanel))
